import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign, TrendingUp, Calendar, LogOut,
  ShoppingCart, Tag, BarChart2, Zap, Clock, Users, CreditCard
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function AdminHeader({ activePage = 'dashboard', activeTab, setActiveTab }) {
  const navigate = useNavigate();
  const token = localStorage.getItem('adminToken');
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API_URL}/admin/stats`, { headers }),
      axios.get(`${API_URL}/orders`, { headers }),
    ]).then(([statsRes, ordersRes]) => {
      setStats(statsRes.data.data);
      setOrders(ordersRes.data.data);
    }).catch(() => {});
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/backoffice');
  };

  const todayRevenue = orders
    .filter(o => {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      return new Date(o.createdAt) >= today && o.paymentStatus === 'paid';
    })
    .reduce((s, o) => s + parseFloat(o.price), 0);

  const todayCount = orders.filter(o => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return new Date(o.createdAt) >= today;
  }).length;

  const monthRevenue = orders
    .filter(o => {
      const start = new Date(); start.setDate(1); start.setHours(0, 0, 0, 0);
      return new Date(o.createdAt) >= start && o.paymentStatus === 'paid';
    })
    .reduce((s, o) => s + parseFloat(o.price), 0);

  const monthCount = orders.filter(o => {
    const start = new Date(); start.setDate(1); start.setHours(0, 0, 0, 0);
    return new Date(o.createdAt) >= start;
  }).length;

  const NAV = [
    { key: 'orders',     label: 'Pedidos',       icon: ShoppingCart, action: () => { if (activePage === 'dashboard') setActiveTab?.('orders'); else navigate('/backoffice/dashboard'); } },
    { key: 'stats',      label: 'Estatísticas',   icon: BarChart2,    action: () => navigate('/backoffice/stats') },
    { key: 'reminders',  label: 'Lembretes',      icon: Clock,        action: () => navigate('/backoffice/reminders') },
    { key: 'apify',      label: 'Apify',          icon: Zap,          action: () => navigate('/backoffice/apify') },
    { key: 'prices',     label: 'Preços',         icon: Tag,          action: () => { if (activePage === 'dashboard') setActiveTab?.('prices'); else navigate('/backoffice/dashboard'); } },
    { key: 'contacts',   label: 'Contactos',      icon: Users,        action: () => navigate('/backoffice/contacts') },
    { key: 'card',       label: 'Cartão',         icon: CreditCard,   action: () => navigate('/backoffice/card-payments') },
  ];

  const isActive = (key) => {
    if (activePage === 'dashboard') return key === (activeTab === 'prices' ? 'prices' : 'orders');
    return key === activePage;
  };

  return (
    <div className="bg-white border-b border-gray-200 shadow-sm">
      <div className="container mx-auto px-4">
        {/* Topo */}
        <div className="flex items-center justify-between py-4">
          <h1 className="text-2xl font-bold text-gray-900">Painel Admin</h1>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
          >
            <LogOut className="w-4 h-4" />
            Sair
          </button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl p-4 shadow-sm text-white">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold opacity-90">Receita Total</p>
              <DollarSign className="w-5 h-5 opacity-75" />
            </div>
            <p className="text-2xl font-bold">€{stats?.totalRevenue?.toFixed(2) || '0.00'}</p>
            <p className="text-xs opacity-75 mt-1">{stats?.totalOrders || 0} pedidos</p>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border-2 border-green-200">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-700">Hoje</p>
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900">€{todayRevenue.toFixed(2)}</p>
            <p className="text-xs text-gray-500 mt-1">{todayCount} pedidos</p>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border-2 border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-700">Este Mês</p>
              <Calendar className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900">€{monthRevenue.toFixed(2)}</p>
            <p className="text-xs text-gray-500 mt-1">{monthCount} pedidos</p>
          </div>
        </div>

        {/* Nav tabs */}
        <div className="flex gap-3 pb-4">
          {NAV.map(({ key, label, icon: Icon, action }) => (
            <button
              key={key}
              onClick={action}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition ${
                isActive(key)
                  ? 'bg-purple-600 text-white shadow-sm'
                  : key === 'reminders'
                    ? 'bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
